/*
 * @Author: changdou.fe@bytedance.com
 * @Date: 2025-08-03 15:10:18
 * @LastEditors: changdou.fe@bytedance.com
 * @LastEditTime: 2026-04-11 11:44:48
 */
'use client';
import { ReactNode, createContext, useEffect, useState, Suspense } from 'react';
import i18next from 'i18next';
import { LOCAL_LANG_COOKIE, LOCAL_THEME_COOKIE, DEFAULT_THEME_COLOR, DEFAULT_THEME, DEFAULT_LANG } from '@/app/const/locale';
import useStorage from '@/app/utils/useStorage';
import { ConfigProvider, theme as antdTheme } from 'antd';
import zhCN from 'antd/locale/zh_CN';
import enUS from 'antd/locale/en_US';
import zhTW from 'antd/locale/zh_TW';
import jaJP from 'antd/locale/ja_JP';
import koKR from 'antd/locale/ko_KR';
import '@/app/assets/i18n/config';
import { useIsLoggedIn, useFetchUserInfo, useUserStore } from '@/app/stores/useUserStore';
import { usePredictStore } from '@/app/stores/usePredictStore';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useGiftVideoPreload } from '@/hooks/use-gift-video-preload';
import dayjs from 'dayjs';
import { goToLogin } from '@/lib/login';
import 'dayjs/locale/zh-cn';
import 'dayjs/locale/zh-tw';
import 'dayjs/locale/ja';
import 'dayjs/locale/ko';
import { isInAppWebView } from '@/lib/utils';

// Context 内容
interface GlobalContextType {
  lang?: string;
  setLang?: (value: string) => void;
  theme?: string;
  setTheme?: (value: string) => void;
  themeColor?: string;
  setThemeColor?: (value: string) => void;
  langReady?: boolean;
}

// 创建 Context
export const GlobalContext = createContext<GlobalContextType>({});
// 白名单路径，这些路径退出的时候不需要刷新和跳转到登录页
export const WHITE_LIST_PATHS = [
  '/discover',
  '/live',
  '/square',
  '/square/detail',
  '/home'
];
// 白名单路径，这些路径退出的时候需要刷新
export const WHITE_LIST_PATHS_WITH_REFRESH = [
  '/live/detail',
];
// 不需要登录验证的页面路径
const PUBLIC_PATHS = [
  'help',
  '/help',
  'chat-invite',
  '/chat-invite',
  'invite',
  '/invite',
  'me/form',
  '/me/form',
  'creator-weekly',
  '/creator-weekly',
  'app-download',
  '/app-download',
  'stream/tools',
  '/stream/tools',
  '/',
  'login', 
  '/login', 
  'article', 
  '/article', 
  'article/detail', 
  '/article/detail',
  'exam',
  '/exam',
  ...WHITE_LIST_PATHS,
  ...WHITE_LIST_PATHS_WITH_REFRESH,
];

// 使用searchParams的内部组件
function GlobalContextContent({ children }: { children: ReactNode }) {
  // 获取url参数
  const [lang, setLang] = useStorage(LOCAL_LANG_COOKIE, DEFAULT_LANG);
  const [theme, setTheme] = useStorage(LOCAL_THEME_COOKIE, DEFAULT_THEME);
  const [themeColor, setThemeColor] = useState(DEFAULT_THEME_COLOR);
  const [langReady, setLangReady] = useState(false);

  // 用户状态管理
  const isLoggedIn = useIsLoggedIn();
  const fetchUserInfo = useFetchUserInfo();
  const { setToken } = useUserStore();
  // 认证守卫相关
  const location = useLocation();
  const pathname = location.pathname;
  const [searchParams] = useSearchParams();
  const [isChecking, setIsChecking] = useState(true);

  // 全局预加载礼物视频（单例模式，只加载一次）
  useGiftVideoPreload(true);

  // 应用启动时拉取 CLOB 版本(纯 GET /version,不依赖钱包登录、不阻塞 UI)
  // 后续所有预测相关的 version 判断都读 store,零 HTTP 等待
  useEffect(() => {
    usePredictStore.getState().fetchVersion();
  }, []);

  // urltoken
  const token = searchParams?.get('access_token');
  const contextValue = {
    lang,
    setLang,
    theme,
    setTheme,
    themeColor,
    setThemeColor,
    langReady,
  };

  // 登录后自动获取用户信息 - 每次路径变化时重新获取
  useEffect(() => {
    if (isInAppWebView()) return;
    if (isLoggedIn) {
      // 每次路径变化时都重新获取用户信息，确保信息是最新的
      fetchUserInfo();
    }
  }, [isLoggedIn, pathname, fetchUserInfo]);

  // 认证守卫逻辑和用户信息检查
  useEffect(() => {
    // 检查当前路径是否为公开路径
    const isPublicPath = PUBLIC_PATHS.some(
      path => path === '/' ? pathname === '/' : pathname.startsWith(path)
    );
    // 如果url上有token，则设置token并停止检查
    if (token) {
      setToken(token);
      setIsChecking(false);
      return;
    }

    // 如果不在公开路径且未登录，重定向到登录页面
    if (!isPublicPath && !isLoggedIn && !token) {
      goToLogin();
      setIsChecking(false);
      return;
    }
    setIsChecking(false);
  }, [isLoggedIn, pathname, token, setToken]);

  // 语言更改
  useEffect(() => {
    const urlLang = searchParams?.get('lang');
    const newLang = urlLang || lang;
    if (urlLang) {
      setLang(urlLang);
    }
    i18next?.changeLanguage(newLang);
    if (!langReady) {
      // 延迟设置langReady
      setTimeout(() => {
        setLangReady(true);
      }, 200);
    }
    document.documentElement.lang = newLang;
  }, [lang, searchParams]);
  // 主题更改
  useEffect(() => {
    const html = document.documentElement;
    // let isDark = false;
    if (theme === 'system') {
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      // isDark = prefersDark;
      html.classList.toggle('dark', prefersDark);
    } else {
      // isDark = theme === 'dark';
      html.classList.toggle('dark', theme === 'dark');
    }
    html.classList.remove('dark-blue', 'dark-black', 'dark-green', 'dark-orange');
    // // 主题颜色更改 - 如果是深色的话，则需要设置主题颜色
    // if (isDark) {
    //   // 移除所有可能的主题颜色类
    //   // 深色主题下，设置主题颜色
    //   html.classList.add(themeColor);
    // }
  }, [theme]);

  // 根据语言代码获取 antd locale 并配置 dayjs locale
  const getAntdLocale = () => {
    const currentLang = searchParams?.get('lang') || lang;

    // 同时配置 dayjs 的 locale
    switch (currentLang) {
      case 'zh-CN':
        dayjs.locale('zh-cn');
        return zhCN;
      case 'en-US':
        dayjs.locale('en');
        return enUS;
      case 'zh-TW':
        dayjs.locale('zh-tw');
        return zhTW;
      case 'ja-JP':
        dayjs.locale('ja');
        return jaJP;
      case 'ko-KR':
        dayjs.locale('ko');
        return koKR;
      default:
        dayjs.locale('en');
        return enUS; // 默认使用英文
    }
  };

  // 认证守卫渲染逻辑
  const renderContent = () => {
    // 在检查期间显示加载状态
    if (isChecking) {
      return null;
    }

    // 如果未登录且不在公开页面，不渲染内容（会被重定向）
    const isPublicPath = PUBLIC_PATHS.some(path => path === '/' ? pathname === '/' : pathname.startsWith(path));
    if (!isLoggedIn && !isPublicPath) {
      return null;
    }

    return children;
  };

  return (
    <GlobalContext.Provider value={contextValue}>
      <ConfigProvider
        locale={getAntdLocale()}
        theme={{
          token: {
            colorPrimary: theme === 'dark' ? '#3f0' : '#35B814',
            colorInfo: theme === 'dark' ? '#3f0' : '#35B814',
          },
          algorithm: theme === 'dark' ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
          components:
            theme === 'dark'
              ? {
                  Input: {
                    activeBorderColor: '#9aa3a4',
                    hoverBorderColor: '#9aa3a4',
                    activeShadow: 'none',
                  },
                  Table: {
                    headerBg: 'transparent', // 表头背景色
                    rowHoverBg: 'rgba(22, 24, 35, 0.6)', // 行悬浮背景
                    colorBgContainer: 'transparent', // 表格主体背景
                  },
                  Slider: {
                    dotActiveBorderColor: '#2CA447',
                    handleColor: '#2CA447',
                    trackBg: '#2CA447',
                  },
                  Modal: {
                    contentBg: '#0c0c0c',
                    headerBg: '#0c0c0c',
                    footerBg: '#0c0c0c',
                  },
                  Tabs: {
                    itemActiveColor: '#fff',
                    itemSelectedColor: '#fff',
                  },
                  Radio: {
                    colorPrimary: '#33ff00',
                    buttonSolidCheckedBg: '#33ff00',
                    buttonSolidCheckedColor: '#000',
                  },
                  Popover: {
                    colorBgElevated: '#1f1f1f',
                  },
                  Tooltip: {
                    colorBgSpotlight: '#1f1f1f',
                  },
                  Menu: {
                    itemSelectedBg: '#ffffff1f',
                    itemActiveBg: '#ffffff1f',
                    colorSplit: 'transparent',
                    colorFillAlter: 'transparent',
                    subMenuItemSelectedColor: '#fff',
                  },
                  Select: {
                    hoverBorderColor: '#9aa3a4',
                    activeOutlineColor: 'transparent',
                    activeBorderColor: '#9aa3a4',
                  },
                  Steps: {
                    colorPrimary: '#33ff00',
                    colorTextLightSolid: '#000',
                  },
                  Segmented: {
                    itemSelectedBg: '#33ff00',
                    itemSelectedColor: '#000',
                  },
                }
              : {},
        }}
      >
        {renderContent()}
      </ConfigProvider>
    </GlobalContext.Provider>
  );
}

// 主要的Provider组件，包装Suspense
export function GlobalContextProvider({ children }: { children: ReactNode }) {
  return (
    <Suspense>
      <GlobalContextContent>{children}</GlobalContextContent>
    </Suspense>
  );
}
